/**
 * Apollo Client configuration for GraphQL queries.
 * Sets up the Apollo Client instance with custom cache policies for different query types,
 * including highlights, reels, EPG data, and EPG by days. Implements custom merge strategies
 * to handle pagination and prevent duplicate data in the cache.
 */

import lodash from 'lodash';
import ApiUtils from '@shared/utils/api';
import { IHighlightResponse } from '@shared/types/interfaces/slider';
import { IReelData, IReelResponse } from '@shared/types/interfaces/rail';
import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  StoreObject,
} from '@apollo/client';
import {
  IEPGResponse,
  IEPGByDaysResponse,
} from '@shared/types/interfaces/event';

/**
 * In-memory cache configuration with custom type policies.
 * Defines merge strategies for paginated queries to properly concatenate results.
 */
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        highlights: {
          // Separate cached results based on variables.
          keyArgs(_args, context) {
            const section = context?.variables?.query?.section;
            const iacorner = context?.variables?.query?.iacorner;
            return [section, iacorner].filter(Boolean).join('-');
          },
          // Concatenate the incoming list items with
          // the existing list items.
          merge(
            existing: IHighlightResponse,
            incoming: IHighlightResponse,
            { readField }
          ) {
            // Obtain existing results and filter repeated values from incoming
            const existingHighlights = existing?.results || [];
            const incomingHighlights =
              incoming?.results?.filter((incomingHighlight) => {
                const found = existing?.results?.find((existingHighlight) => {
                  const existingId = readField(
                    'id',
                    existingHighlight as unknown as StoreObject
                  );
                  const incomingId = readField(
                    'id',
                    incomingHighlight as unknown as StoreObject
                  );
                  return existingId === incomingId;
                });
                return !found;
              }) || [];

            // Return merged object
            return {
              ...(existing || incoming || {}),
              results: [...existingHighlights, ...incomingHighlights],
            };
          },
        },
        reels: {
          // Separate cached results based on variables.
          keyArgs(_args, context) {
            const section = context?.variables?.query?.section;
            const iacorner = context?.variables?.query?.iacorner;
            return section ? section : iacorner;
          },
          // Concatenate the incoming list items with
          // the existing list items.
          merge(
            existing: IReelResponse,
            incoming: IReelResponse,
            { readField }
          ) {
            try {
              // Create copy of existing and incoming reels
              const existingReels = existing?.results;
              let incomingReels = incoming?.results;
              let results: IReelData[] = [];

              // Iterate existing reels looking for matches between responses
              existingReels?.forEach((existingReel) => {
                // Check if current reel exists in incoming reels
                const commonReel = incomingReels?.find(
                  (incomingReel) =>
                    existingReel.mediaSection.name ===
                    incomingReel.mediaSection.name
                );

                // Filter duplicates and populate shows
                if (commonReel) {
                  // Filter common reel from incoming data
                  incomingReels = incomingReels.filter(
                    (incomingReel) =>
                      incomingReel.mediaSection.name !==
                      commonReel.mediaSection.name
                  );

                  // Filter function
                  const filterItems = (
                    data:
                      | typeof commonReel.addons
                      | typeof commonReel.corners
                      | typeof commonReel.shows,
                    key: 'addons' | 'corners' | 'shows'
                  ) => {
                    return (
                      data.filter((commonItem) => {
                        const found = existingReel[key]?.find(
                          (existingItem) => {
                            const existingId = readField(
                              '_id',
                              existingItem as unknown as StoreObject
                            );
                            const commonId = readField(
                              '_id',
                              commonItem as unknown as StoreObject
                            );
                            return existingId === commonId;
                          }
                        );
                        return !found;
                      }) || []
                    );
                  };

                  // Join data to populate
                  let reelData = {};
                  const key = commonReel?.shows?.length
                    ? 'shows'
                    : commonReel?.addons?.length
                    ? 'addons'
                    : commonReel?.corners?.length
                    ? 'corners'
                    : '';
                  if (key) {
                    reelData = {
                      [key]: [
                        ...existingReel[key],
                        ...filterItems(commonReel[key], key),
                      ],
                    };
                  }

                  // Append shows from common reel to existing reel
                  existingReel = {
                    ...existingReel,
                    ...reelData,
                  };
                }

                // Push mutated reel into results
                results.push(existingReel);
              });

              // Calculate maximum rail position from previous results
              // to keep reels sorted
              const maxPosition = results.reduce(
                (prev, current) =>
                  prev < (current.mediaSection.position || 0)
                    ? current.mediaSection.position || 0
                    : prev,
                0
              );

              // Append incoming reels leftovers to results
              results = [
                ...(results || []),
                ...incomingReels.map((value) => ({
                  ...value,
                  mediaSection: {
                    ...value.mediaSection,
                    position: maxPosition + (value.mediaSection.position || 1),
                  },
                })),
              ];

              // Return merged object
              return {
                ...(existing || incoming || {}),
                results,
              };
            } catch (error) {
              console.error(error);
              return existing;
            }
          },
        },
        epgs: {
          // Separate cached results based on channel id list.
          keyArgs(_args, context) {
            return context?.variables?.query?.channelIds?.length
              ? context?.variables?.query?.channelIds[0]
              : '';
          },
          // Merge the existing and the incoming response. Deep clone is required.
          merge(existing: IEPGResponse, incoming: IEPGResponse, { readField }) {
            try {
              let result: IEPGResponse = incoming;
              // If existing data, append incoming data
              if (existing) {
                result = lodash.cloneDeep(existing);
                incoming?.items?.forEach((item) => {
                  const found = result?.items?.find(
                    (value) => value.channel === item.channel
                  );
                  // If key is found, append epgs
                  if (found) {
                    item.epgs.forEach((item) => {
                      // To retrieve raw values, use readField
                      const incomingId = readField(
                        '_id',
                        item as unknown as StoreObject
                      );
                      if (
                        !found.epgs.find((item) => {
                          // Check existing ids using readField
                          const existingId = readField(
                            '_id',
                            item as unknown as StoreObject
                          );
                          return incomingId === existingId;
                        })
                      ) {
                        found.epgs.push(item);
                      }
                    });
                  } else {
                    result.items.push(item);
                  }
                });
              }
              return result;
            } catch (e) {
              console.error(e);
              return existing;
            }
          },
        },
        epgsByDays: {
          // Separate cached results based on channel id list.
          keyArgs(_args, context) {
            return context?.variables?.query?.channelId;
          },
          // Merge the existing and the incoming response. Deep clone is required.
          merge(
            existing: IEPGByDaysResponse,
            incoming: IEPGByDaysResponse,
            { readField }
          ) {
            try {
              let result: IEPGByDaysResponse = incoming;
              // If existing data, append incoming data
              if (existing) {
                result = lodash.cloneDeep(existing);
                incoming?.items?.forEach((item) => {
                  const found = result?.items?.find(
                    (value) => value.day === item.day
                  );
                  // If key is found, append epgs
                  if (found) {
                    item.epgs.forEach((item) => {
                      // To retrieve raw values, use readField
                      const incomingId = readField(
                        '_id',
                        item as unknown as StoreObject
                      );
                      if (
                        !found.epgs.find((item) => {
                          // Check existing ids using readField
                          const existingId = readField(
                            '_id',
                            item as unknown as StoreObject
                          );
                          return incomingId === existingId;
                        })
                      ) {
                        found.epgs.push(item);
                      }
                    });
                  } else {
                    result.items.push(item);
                  }
                });
              }
              return result;
            } catch (e) {
              console.error(e);
              return existing;
            }
          },
        },
      },
    },
  },
});

const client = new ApolloClient({
  cache,
  link: new HttpLink({ uri: ApiUtils.getUrl('graphQLUrl') }),
});

export default client;
